import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { useQueryClient } from "@tanstack/react-query";
import { useGetProfileDefinition } from "../../../../api/queries";
import { useUpdateProfileDefinition } from "../../../../api/mutations";
import { useNotification } from "../../../../hooks/useNotification";
import Container from "../../../../componentLibrary/Container";
import Button from "../../../../componentLibrary/Button";
import Spinner from "../../../../componentLibrary/Spinner";
import type { ToolboxQuestionI } from "../../../../types";
import ReorderableVerticalList from "../../../../componentLibrary/ReorderableVerticalList";
import RbdFullRowDragSurface from "../../../../componentLibrary/RbdFullRowDragSurface";
import QuestionTypeList from "./components/QuestionTypeList";
import QuestionCard from "./components/questionCards/QuestionCard";
import { getNewQuestion, QuestionType } from "./utils/questionUtils";

export default function EditProfileDefinitionPage() {
  const { topicId, definitionId } = useParams<{
    topicId: string;
    definitionId: string;
  }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { notify } = useNotification();

  const {
    data: definition,
    isLoading,
    isError,
  } = useGetProfileDefinition(definitionId ?? "");

  const [questions, setQuestions] = useState<ToolboxQuestionI[] | null>(null);

  const draft = questions ?? definition?.questions ?? [];

  const saveMutation = useUpdateProfileDefinition({
    onSuccess: async () => {
      notify("success", "Profile definition saved");
      await queryClient.invalidateQueries({
        queryKey: ["toolboxProfileDefinitions", topicId],
      });
      if (definitionId) {
        await queryClient.invalidateQueries({
          queryKey: ["toolboxProfileDefinition", definitionId],
        });
      }
      navigate(`/admin/toolboxes/${topicId}/profile-definitions`);
    },
    onError: () => notify("error", "Failed to save profile definition"),
  });

  const handleAdd = (type: QuestionType) => {
    setQuestions([...draft, getNewQuestion(type)]);
  };

  const handleChange = (updated: ToolboxQuestionI) => {
    setQuestions(
      draft.map((q) => (q.questionId === updated.questionId ? updated : q)),
    );
  };

  const handleRemove = (questionId: string) => {
    setQuestions(draft.filter((q) => q.questionId !== questionId));
  };

  const handleSave = () => {
    if (!definitionId) return;
    saveMutation.mutate({ definitionId, questions: draft });
  };

  const backPath = `/admin/toolboxes/${topicId}/profile-definitions`;

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner className="w-8" />
      </div>
    );
  }

  if (isError || !definition) {
    return (
      <div className="p-4 text-sm text-salmon-400">
        Profile definition not found.
      </div>
    );
  }

  return (
    <div className="pt-4">
      <div className="mb-4 flex items-center justify-between">
        <button
          type="button"
          onClick={() => navigate(backPath)}
          className="flex items-center gap-1.5 text-sm text-electrum-300 hover:text-electrum transition-colors mb-4 w-fit"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Back to Profile Definitions
        </button>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">
            {definition.displayName?.trim() || `v${definition.version}`} ·{" "}
            {draft.length} question
            {draft.length !== 1 ? "s" : ""}
          </span>
          <Button
            type="button"
            size="sm"
            loading={saveMutation.isPending}
            onClick={handleSave}
          >
            Save
          </Button>
        </div>
      </div>
      {definition.isActive && (
        <p className="text-sm text-amber-900 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 mb-4">
          This is the <span className="font-semibold">live</span> profile for
          all users. Saving applies immediately.
        </p>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <Container className="lg:col-span-3 self-start">
          <QuestionTypeList onAdd={handleAdd} />
        </Container>

        <div className="lg:col-span-9">
          <Container>
            {draft.length === 0 ? (
              <div className="flex items-center justify-center py-16">
                <p className="text-sm text-gray-400">
                  Add a question from the panel on the left.
                </p>
              </div>
            ) : (
              <ReorderableVerticalList
                items={draft}
                getId={(q) => q.questionId}
                onReorder={setQuestions}
                droppableId="toolbox-profile-definition-questions"
                className="space-y-3"
              >
                {(q, i, provided) => (
                  <RbdFullRowDragSurface provided={provided}>
                    <QuestionCard
                      question={q}
                      index={i}
                      onChange={handleChange}
                      onRemove={() => handleRemove(q.questionId)}
                    />
                  </RbdFullRowDragSurface>
                )}
              </ReorderableVerticalList>
            )}
          </Container>
        </div>
      </div>
    </div>
  );
}
